import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router';
import { Plus, Pencil, Trash2, List, GitBranch } from 'lucide-react';
import ConnectionForm from '../../components/data-integration/ConnectionForm';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from '@/components/ui/table';
import {
  getDbConnectionList,
  deleteDbConnection,
  type DbConnectionInfo,
} from '../../services/data-integration-api';

export default function ConnectionManager() {
  const navigate = useNavigate();
  const [connections, setConnections] = useState<DbConnectionInfo[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [loading, setLoading] = useState(false);
  const [filterType, setFilterType] = useState<'source' | 'target' | 'production' | ''>('');
  const [showForm, setShowForm] = useState(false);
  const [editConnection, setEditConnection] = useState<DbConnectionInfo | null>(null);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const params: any = { page, pageSize };
      if (filterType) params.connection_type = filterType;
      const result = await getDbConnectionList(params);
      setConnections(result.items);
      setTotal(result.total);
    } catch (e) {
      console.error('获取连接列表失败:', e);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, filterType]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const handleDelete = async (conn: DbConnectionInfo) => {
    if (!window.confirm(`确定删除连接「${conn.connection_name}」？`)) return;
    try {
      await deleteDbConnection(conn._id);
      fetchList();
    } catch (e: any) {
      alert('删除失败: ' + e.message);
    }
  };

  const handleEdit = (conn: DbConnectionInfo) => {
    setEditConnection(conn);
    setShowForm(true);
  };

  const handleAdd = () => {
    setEditConnection(null);
    setShowForm(true);
  };

  const handleSaved = () => {
    setShowForm(false);
    setEditConnection(null);
    fetchList();
  };

  const statusLabel = (status: string) => {
    const map: Record<string, string> = {
      untested: '未测试',
      connected: '已连接',
      failed: '连接失败',
    };
    return map[status] || status;
  };

  const statusVariant = (status: string): 'default' | 'destructive' | 'secondary' => {
    if (status === 'connected') return 'default';
    if (status === 'failed') return 'destructive';
    return 'secondary';
  };

  const dbTypeLabel = (type: string) => {
    return type === 'mysql' ? 'MySQL' : type === 'postgresql' ? 'PostgreSQL' : type;
  };

  if (showForm) {
    return (
      <div className="p-6">
        <ConnectionForm
          connection={editConnection}
          onSaved={handleSaved}
          onCancel={() => { setShowForm(false); setEditConnection(null); }}
        />
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between border-b border-border bg-card px-6 py-3">
        <h2 className="text-base font-semibold">数据库连接管理</h2>
        <Button onClick={handleAdd}>
          <Plus className="h-4 w-4" />
          新建连接
        </Button>
      </div>

      <div className="p-6">
        <div className="mb-4 flex items-center gap-2">
          <Label>筛选</Label>
          <select
            value={filterType}
            onChange={e => { setFilterType(e.target.value as any); setPage(1); }}
            className="h-9 rounded-md border border-input bg-background px-3 text-sm"
          >
            <option value="">全部</option>
            <option value="source">源库</option>
            <option value="target">目标库</option>
            <option value="production">生产库</option>
          </select>
        </div>

        {loading ? (
          <p className="text-muted-foreground">加载中...</p>
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>连接名称</TableHead>
                  <TableHead>类型</TableHead>
                  <TableHead>数据库</TableHead>
                  <TableHead>主机</TableHead>
                  <TableHead>状态</TableHead>
                  <TableHead>型录</TableHead>
                  <TableHead>操作</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {connections.map(conn => (
                  <TableRow key={conn._id}>
                    <TableCell>{conn.connection_name}</TableCell>
                    <TableCell>{conn.connection_type === 'source' ? '源库' : conn.connection_type === 'production' ? '生产库' : '目标库'}</TableCell>
                    <TableCell>{dbTypeLabel(conn.db_type)}</TableCell>
                    <TableCell className="font-mono">{conn.host}:{conn.port}</TableCell>
                    <TableCell>
                      <Badge variant={statusVariant(conn.connection_status)}>
                        {statusLabel(conn.connection_status)}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {conn.connection_type === 'source' ? (
                        conn.catalog_table_count && conn.catalog_table_count > 0 ? (
                          <span className="text-sm">
                            <Badge variant="secondary">{conn.catalog_table_count} 张表</Badge>
                            <span className="ml-1 text-muted-foreground">v{conn.catalog_version}</span>
                          </span>
                        ) : (
                          <span className="text-sm text-muted-foreground">未构建</span>
                        )
                      ) : (
                        <span className="text-sm text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        {conn.connection_type === 'source' && (
                          <>
                            <Button
                              variant="ghost"
                              size="sm"
                              title="查看型录"
                              onClick={() => navigate(`/data-integration/catalog/${conn._id}`)}
                            >
                              <List className="h-3.5 w-3.5" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              title="ER图"
                              onClick={() => navigate(`/data-integration/er/${conn._id}`)}
                            >
                              <GitBranch className="h-3.5 w-3.5" />
                            </Button>
                          </>
                        )}
                        <Button variant="ghost" size="sm" onClick={() => handleEdit(conn)}>
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(conn)}>
                          <Trash2 className="h-3.5 w-3.5 text-destructive" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {connections.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-6 text-muted-foreground">
                      暂无连接配置
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>

            {total > pageSize && (
              <div className="mt-4 flex items-center gap-2">
                <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
                  上一页
                </Button>
                <span className="text-sm text-muted-foreground">
                  第 {page} 页 / 共 {Math.ceil(total / pageSize)} 页
                </span>
                <Button variant="outline" size="sm" disabled={page >= Math.ceil(total / pageSize)} onClick={() => setPage(p => p + 1)}>
                  下一页
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
